import React, { useState, useRef } from 'react';
import { apiCall } from '../../utils/apiUtils';

const API_BASE = '/api/external/v1/live-stamping';

const LiveStampingUploadModal = ({ order, onClose, onComplete }) => {
  const [phase, setPhase] = useState('idle'); // idle, uploading, done
  const [progress, setProgress] = useState(0);
  const [outcome, setOutcome] = useState(null); // success, error
  const [errorMsg, setErrorMsg] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const fileInputRef = useRef(null);

  if (!order) return null;

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        setErrorMsg('Invalid file type. Please upload PDF, JPEG, or PNG.');
        return;
      }
      // Validate size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setErrorMsg('File too large. Maximum size is 10MB.');
        return;
      }
      setSelectedFile(file);
      setErrorMsg('');
    }
  };

  const startUpload = async () => {
    if (!selectedFile) {
      setErrorMsg('Please select a file first.');
      return;
    }

    setPhase('uploading');
    setProgress(10);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', selectedFile);

      // Use XMLHttpRequest for progress tracking
      const xhr = new XMLHttpRequest();
      
      await new Promise((resolve, reject) => {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percent = Math.round((event.loaded / event.total) * 80) + 10; // 10-90%
            setProgress(percent);
          }
        });

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            try {
              const error = JSON.parse(xhr.responseText);
              reject(new Error(error.detail || 'Upload failed'));
            } catch {
              reject(new Error('Upload failed'));
            }
          }
        };

        xhr.onerror = () => reject(new Error('Network error'));

        xhr.open('POST', `${API_BASE}/orders/${order.id}/upload`);
        xhr.withCredentials = true; // Include cookies for auth
        xhr.send(formData);
      });

      setProgress(100);
      setOutcome('success');
      setPhase('done');
      
      // Notify parent to refresh orders
      if (onComplete) {
        onComplete(order.id, 'pending_verification');
      }

    } catch (err) {
      console.error('Upload error:', err);
      setErrorMsg(err.message || 'Upload failed. Please try again.');
      setOutcome('error');
      setPhase('done');
    }
  };

  const handleClose = () => {
    setPhase('idle');
    setProgress(0);
    setOutcome(null);
    setSelectedFile(null);
    setErrorMsg('');
    onClose();
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file) {
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        setErrorMsg('Invalid file type. Please upload PDF, JPEG, or PNG.');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setErrorMsg('File too large. Maximum size is 10MB.');
        return;
      }
      setSelectedFile(file);
      setErrorMsg('');
    }
  };

  // Get first party name from stamps array
  const getFirstPartyName = () => {
    if (order.stamps && order.stamps.length > 0) {
      return order.stamps[0]?.first_party?.name || 'N/A';
    }
    return order.first_party?.name || 'N/A';
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={handleClose}
            className="p-1.5 -ml-1 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-all focus:outline-none"
            aria-label="Back"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
          </button>
          <h3 className="font-bold text-gray-800">Upload Stamped Document</h3>
        </div>

        <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm">
          <p><strong>Order:</strong> #{order.id}</p>
          <p><strong>Service:</strong> {order.service_name || order.serviceName}</p>
          <p><strong>Party:</strong> {getFirstPartyName()}</p>
        </div>

        {errorMsg && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {errorMsg}
          </div>
        )}

        {phase === 'idle' && (
          <>
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileSelect}
              accept=".pdf,.jpg,.jpeg,.png"
              className="hidden"
            />
            <div 
              onClick={() => fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className="border-2 border-dashed border-indigo-200 bg-indigo-50/50 rounded-xl p-8 flex flex-col items-center cursor-pointer hover:bg-indigo-50 transition-colors"
            >
              <svg className="w-12 h-12 text-indigo-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              {selectedFile ? (
                <div className="text-center">
                  <p className="font-medium text-indigo-900">{selectedFile.name}</p>
                  <p className="text-xs text-gray-500 mt-1">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                </div>
              ) : (
                <>
                  <p className="font-medium text-indigo-900">Click or drag to upload</p>
                  <p className="text-xs text-gray-500 mt-1">PDF, JPEG, PNG (max 10MB)</p>
                </>
              )}
            </div>
            
            {selectedFile && (
              <button 
                onClick={startUpload}
                className="mt-4 w-full bg-indigo-600 text-white py-3 rounded-xl font-semibold hover:bg-indigo-700 transition-colors"
              >
                Upload Document
              </button>
            )}
          </>
        )}

        {phase === 'uploading' && (
          <div className="py-8 text-center">
            <p className="font-bold text-gray-800 mb-4">Uploading...</p>
            <div className="h-3 w-full bg-gray-200 rounded-full overflow-hidden mb-2">
              <div 
                className="h-full rounded-full transition-all bg-indigo-500" 
                style={{width: `${progress}%`}} 
              />
            </div>
            <p className="text-xs text-gray-400">{progress}%</p>
          </div>
        )}

        {phase === 'done' && (
          <div className="py-6 text-center">
            {outcome === 'success' ? (
              <>
                <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h4 className="text-lg font-bold text-green-700">Upload Complete!</h4>
                <p className="text-gray-500 text-sm">Document sent for admin verification</p>
              </>
            ) : (
              <>
                <div className="mx-auto h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h4 className="text-lg font-bold text-red-700">Upload Failed</h4>
                <p className="text-gray-500 text-sm">{errorMsg}</p>
              </>
            )}
            <button onClick={handleClose} className="mt-6 w-full bg-gray-900 text-white py-2 rounded-lg font-medium">Done</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveStampingUploadModal;
